/**
 * Typed wrappers for the Loans & Advances module (migration 064).
 *
 * Paths + shapes taken VERBATIM from backend/internal/router/router.go +
 * handlers.LoanHandler. Cookie-only auth + CSRF + 401→refresh are handled
 * by the shared axios instance (lib/api/client.ts). No client-supplied
 * tenant_id / user_id is ever sent — the server derives both from the
 * session.
 *
 * RBAC (server-enforced; mirrored in the UI for UX only):
 *   - employee: self-request, read/cancel OWN loans.
 *   - finance (company_admin, hr_manager, accountant): approve, reject,
 *     disburse, prepay, write-off, see all.
 *
 * Step-up TOTP (the client.ts 401 totp_required short-circuit → TOTP
 * modal → retry) is server-enforced on /disburse + /write-off ONLY.
 */

import { api } from "./client";

export const LOANS_BASE = "/api/v1/loans";

// ---------------------------------------------------------------------------
// Types (mirror models.Loan / LoanInstallment / LoanEvent)
// ---------------------------------------------------------------------------

export type LoanKind = "loan" | "advance";

export type LoanStatus =
  | "requested"
  | "approved"
  | "rejected"
  | "cancelled"
  | "disbursed"
  | "closed"
  | "written_off";

export type LoanInstallmentStatus =
  | "scheduled"
  | "paid"
  | "skipped"
  | "waived";

export type LoanEventKind =
  | "disburse"
  | "emi_recovery"
  | "prepayment"
  | "skip"
  | "waiver"
  | "ff_recovery"
  | "write_off"
  | "reschedule";

export interface Loan {
  id: number;
  tenant_id: number;
  entity_id: number;
  employee_id: number;
  kind: LoanKind;
  principal_paise: number;
  tenure_months: number;
  interest_rate_bps: number;
  interest_method: string;
  first_recovery_year?: number;
  first_recovery_month?: number;
  reason?: string;
  status: LoanStatus;
  requested_by_id: number;
  approved_by_id?: number;
  approved_at?: string;
  rejected_reason?: string;
  disbursed_by_id?: number;
  disbursed_at?: string;
  disburse_journal_entry_id?: number;
  written_off_journal_entry_id?: number;
  outstanding_paise: number;
  closed_at?: string;
  created_at: string;
  updated_at: string;
  employee_name?: string;
}

export interface LoanInstallment {
  id: number;
  tenant_id: number;
  entity_id: number;
  loan_id: number;
  seq_no: number;
  due_year: number;
  due_month: number;
  principal_paise: number;
  interest_paise: number;
  amount_paise: number;
  outstanding_after_paise: number;
  status: LoanInstallmentStatus;
  paid_payroll_run_id?: number;
  paid_amount_paise?: number;
  paid_at?: string;
  repay_journal_entry_id?: number;
  created_at: string;
  updated_at: string;
}

export interface LoanEvent {
  id: number;
  tenant_id: number;
  entity_id: number;
  loan_id: number;
  kind: LoanEventKind;
  amount_paise: number;
  outstanding_after_paise: number;
  journal_entry_id?: number;
  payroll_run_id?: number;
  note?: string;
  actor_id: number;
  created_at: string;
}

export interface LoanSummary {
  employee_id: number;
  disbursed_count: number;
  outstanding_paise: number;
}

export interface PreviewInstallment {
  SeqNo: number;
  DueYear: number;
  DueMonth: number;
  PrincipalPaise: number;
  InterestPaise: number;
  AmountPaise: number;
  OutstandingAfterPaise: number;
}

export interface CreateLoanRequest {
  kind: LoanKind;
  principal_paise: number;
  tenure_months: number;
  reason?: string;
  first_recovery_year?: number;
  first_recovery_month?: number;
}

export interface LoanDetail {
  loan: Loan;
  installments: LoanInstallment[];
  events: LoanEvent[];
  perquisite_disclaimer: boolean;
  /**
   * The caller is the borrower AND the workspace's only finance user —
   * nobody else can act, so they may approve (requested) / disburse
   * (approved) their own loan explicitly; audited server-side.
   */
  can_self_approve?: boolean;
  can_self_disburse?: boolean;
  /** The workspace's only finance user, on their own loan disbursed while
   *  the books were off: POST …/post-disbursement {"self_post": true}. */
  can_self_post_books?: boolean;
  /**
   * Reductions of the outstanding with no journal of their own (the books
   * were off) — computed by the server (2026-10 audit Q16):
   * backlog = EMI / F&F recoveries whose payroll run / F&F is not posted
   * yet (Accounting → Post to books books them — never by hand);
   * manual = prepayments / write-offs made while the books were off (no
   * backlog covers them). Absent on an older API build.
   */
  unbooked_backlog_paise?: number;
  unbooked_manual_paise?: number;
}

export interface PreviewScheduleResponse {
  installments: PreviewInstallment[];
  perquisite_disclaimer: boolean;
}

interface LoanResponse {
  loan: Loan;
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/** POST /loans — employee self-request. An advance forces tenure 1 server-side. */
export async function createLoan(body: CreateLoanRequest): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(LOANS_BASE, body);
  return data.loan;
}

/** POST /loans/on-behalf — HR records a loan / advance for an employee
 * (lands 'requested', like a self-request; the employee is notified).
 * Errors: 400 invalid_employee (inactive / unknown / yourself) · 403 ·
 * 409 active_loan_exists · 422 validation. */
export interface CreateLoanOnBehalfRequest extends CreateLoanRequest {
  employee_id: number;
}

export async function createLoanOnBehalf(body: CreateLoanOnBehalfRequest): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(`${LOANS_BASE}/on-behalf`, body);
  return data.loan;
}

/** GET /loans?status=&employee_id=[&exclude_self=1] — role-filtered
 * (non-finance sees own). `exclude_self=1` narrows an approver's list to
 * loans they can ACT on (own requests removed). */
export async function listLoans(
  params?: {
    status?: LoanStatus;
    employee_id?: number;
    exclude_self?: boolean;
  },
  signal?: AbortSignal,
): Promise<Loan[]> {
  const { exclude_self, ...rest } = params ?? {};
  const { data } = await api.get<{ loans: Loan[] }>(LOANS_BASE, {
    params: { ...rest, ...(exclude_self ? { exclude_self: 1 } : {}) },
    signal,
  });
  return data.loans ?? [];
}

/** GET /loans/{id} — loan + schedule + events + perquisite flag. */
export async function getLoan(
  id: number,
  signal?: AbortSignal,
): Promise<LoanDetail> {
  const { data } = await api.get<LoanDetail>(`${LOANS_BASE}/${id}`, { signal });
  return data;
}

/** GET /loans/{id}/schedule — the installment schedule alone. */
export async function getSchedule(
  id: number,
  signal?: AbortSignal,
): Promise<LoanInstallment[]> {
  const { data } = await api.get<{ installments: LoanInstallment[] }>(
    `${LOANS_BASE}/${id}/schedule`,
    { signal },
  );
  return data.installments ?? [];
}

/** POST /loans/{id}/cancel — borrower only (requested → cancelled). */
export async function cancelLoan(id: number): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(`${LOANS_BASE}/${id}/cancel`, {});
  return data.loan;
}

/**
 * POST /loans/{id}/approve — finance (requested → approved). `self_approve`
 * is the explicit opt-in for the workspace's only finance user approving
 * their OWN loan (audited server-side as loan.self_approved).
 */
export async function approveLoan(
  id: number,
  body: { self_approve?: boolean } = {},
): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(`${LOANS_BASE}/${id}/approve`, body);
  return data.loan;
}

/** POST /loans/{id}/reject — finance; reason required. */
export async function rejectLoan(id: number, reason: string): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(`${LOANS_BASE}/${id}/reject`, {
    reason,
  });
  return data.loan;
}

/**
 * POST /loans/{id}/disburse — finance; STRICT step-up TOTP server-side.
 * An unenrolled caller 403s totp_enrollment_required; an enrolled-not-
 * stepped-up session 401s totp_required (client.ts routes to the TOTP modal
 * and retries). Posts DR 1210 / CR 1110 + generates the EMI schedule.
 * `self_disburse` is the sole finance user's explicit opt-in for their
 * OWN loan (audited as loan.self_disbursed).
 */
export async function disburseLoan(
  id: number,
  body: { self_disburse?: boolean } = {},
): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(
    `${LOANS_BASE}/${id}/disburse`,
    body,
  );
  return data.loan;
}

/** POST /loans/{id}/prepay — finance; re-amortises + posts DR 1110 / CR 1210. */
export async function prepayLoan(
  id: number,
  amountPaise: number,
  note?: string,
): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(`${LOANS_BASE}/${id}/prepay`, {
    amount_paise: amountPaise,
    note,
  });
  return data.loan;
}

/**
 * POST /loans/{id}/write-off — finance; STRICT step-up TOTP server-side.
 * Posts DR 5400 / CR 1210 for the remaining outstanding (disbursed →
 * written_off).
 */
export async function writeOffLoan(id: number, note?: string): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(
    `${LOANS_BASE}/${id}/write-off`,
    { note },
  );
  return data.loan;
}

/**
 * POST /loans/{id}/post-disbursement — finance, Accounting on. Books the
 * missing DR 1210 / CR 1110 journal of a loan disbursed while the books
 * were off (2026-10 browser QA, books #14). `date` (YYYY-MM-DD) defaults
 * server-side to the disbursement day; pick an open period when that one
 * is locked (422 period_locked).
 */
export async function postLoanDisbursement(
  id: number,
  date?: string,
  selfPost?: boolean,
): Promise<Loan> {
  const { data } = await api.post<LoanResponse>(
    `${LOANS_BASE}/${id}/post-disbursement`,
    { ...(date ? { date } : {}), ...(selfPost ? { self_post: true } : {}) },
  );
  return data.loan;
}

/** GET /loans/summary?employee_id= — outstanding rollup. */
export async function getLoanSummary(
  employeeId?: number,
  signal?: AbortSignal,
): Promise<LoanSummary> {
  const { data } = await api.get<LoanSummary>(`${LOANS_BASE}/summary`, {
    params: employeeId != null ? { employee_id: employeeId } : undefined,
    signal,
  });
  return data;
}

/**
 * POST /loans/preview-schedule — dry-run EMI preview (single source of
 * truth = the backend engine). Used by the request form's live preview.
 */
export async function previewSchedule(body: {
  principal_paise: number;
  tenure_months: number;
  first_recovery_year?: number;
  first_recovery_month?: number;
}): Promise<PreviewScheduleResponse> {
  const { data } = await api.post<PreviewScheduleResponse>(
    `${LOANS_BASE}/preview-schedule`,
    body,
  );
  return data;
}
